<template>
  <div class="d-flex flex-column">
    <div class="small text-medium-emphasis">{{ $t('SUBPROJECTS.MANAGER.DESCRIPTION') }}</div>

    <div class="d-flex align-center mt-2 ml-7 gap-4">
      <select-filter v-model="filterCategory" :items="projectCategories" :placeholder="$t('PROJECTS.CATEGORY', 1)"
                     prepend-inner-icon="far fa-tags" prepend-icon="far fa-filter" clearable style="width: 250px">
        <template #selection="{ item: { value } }">
          <project-category :category="value" density="compact" style="max-width: 100%; position: relative; top: -1px"></project-category>
        </template>
        <template #item="{ props, item: { value } }">
          <v-list-item density="comfortable" v-bind="props">
            <template #title>
              <project-category :category="value" pointer v-bind="props"></project-category>
            </template>
          </v-list-item>
        </template>
      </select-filter>
      <text-filter v-model="filterTitle" :label="$t('PROJECTS.SEARCH_PROJECT_TITLE')"
                   prepend-inner-icon="far fa-search" clearable style="width: 250px">
      </text-filter>
    </div>

    <v-list v-model:selected="selectedManagedProjectIds" select-strategy="classic" variant="elevated" density="compact" class="py-1">
      <filter-toggle class="font-weight-bold" :selected-ids="selectedManagedProjectIds" :sublist-ids="filteredManagedProjectIds" style="margin-left: -2px"
                     @update="selectedManagedProjectIds = $event">
        {{ $t('SUBPROJECTS.MANAGER.ALL_PROJECTS_IN_LIST') }} ({{ filteredManagedProjectIds.length }})
      </filter-toggle>
      <v-divider></v-divider>
      <div style="min-height: 160px; max-height: calc(100vh - 496px); overflow-y: auto;">
        <v-list-item v-for="project in filteredManagedProjects" :key="project.id" :value="project.id">
          <template #prepend="{ isSelected, select }">
            <v-checkbox-btn :model-value="isSelected" density="compact" class="ml-1 mr-2" @update:model-value="select"></v-checkbox-btn>
          </template>
          <v-list-item-title>{{ project.title || $t('PLANNING.UNNAMED_PROJECT') }}</v-list-item-title>
          <v-list-item-subtitle v-if="project.category">
            <project-category :category="project.category" density="compact" style="max-width: 100%; margin-top: 2px"></project-category>
          </v-list-item-subtitle>
        </v-list-item>
      </div>
    </v-list>

    <div v-if="selectedManagedProjectIds.length" class="small text-medium-emphasis mt-2">
      {{ $t('SUBPROJECTS.MANAGER.TOTAL', { count: selectedManagedProjectIds.length }) }}
    </div>

    <b class="text-primary mt-6">{{ $t('GLOBAL.OPTIONS') }}</b>
    <div class="px-6 py-1">
      <field-label :label="$t('SUBPROJECTS.MANAGER.NEW_SUBPROJECTS_LANES')">
        <v-select v-model="newSubprojectsLanesCount" :items="newSubprojectsLanesCountOptions" prepend-inner-icon="far fa-th-list" style="width: 250px"></v-select>
      </field-label>
    </div>

    <div class="text-center mt-6">
      <v-btn color="primary" rounded :min-width="180" :loading="managingSubprojects" @click="manageSubprojects()">
        {{ $t('GLOBAL.APPLY_MODIFICATIONS') }}
      </v-btn>
      <div v-if="manageError" class="small text-errorred mt-1">{{ manageError }}</div>
      <div class="small text-medium-emphasis mt-1">
        {{ $t('SUBPROJECTS.MANAGER.TO_ADD') }} : {{ managedProjectsToAdd.length }} -
        {{ $t('SUBPROJECTS.MANAGER.TO_REMOVE') }} : {{ managedLanesToDelete.length }}
      </div>
    </div>
  </div>
</template>

<script>
  import ProjectSrv from '@/components/Projects/ProjectSrv';
  import ProjectCategory from '@/components/Projects/ProjectCategory.vue';
  import usePlanningLanesStore from '@/stores/planning/lanes';

  export default {
    components: {
      ProjectCategory,
    },
    props: {
      planning: { type: Object, required: true },
      projects: { type: Array, default: () => ([]) },
    },
    emits: ['close'],
    data() {
      return {
        filterCategory: null,
        filterTitle: '',
        selectedManagedProjectIds: [],
        newSubprojectsLanesCount: 1,
        newSubprojectsLanesCountOptions: [
          { value: 1, title: this.$t('SUBPROJECTS.MANAGER.FIRST_LANE') },
          { value: 2, title: this.$t('SUBPROJECTS.MANAGER.FIRST_TWO_LANES') },
          { value: null, title: this.$t('SUBPROJECTS.MANAGER.ALL_LANES') },
        ],
        managingSubprojects: false,
        manageError: '',
        planningLanesStore: usePlanningLanesStore(),
      };
    },
    computed: {
      availableProjects() {
        return this.projects.filter(item => item.id != this.planning.id);
      },
      existingSubprojectLanes() {
        return this.planning.lanes?.filter(lane => lane.project_id) || [];
      },
      existingSubprojectProjectIds() {
        return this.existingSubprojectLanes.map(lane => lane.project_id);
      },
      projectCategories() {
        const categories = _.union(...this.availableProjects.map(project => this.getProjectCategories(project)));
        return categories.sort((a, b) => a.localeCompare(b));
      },
      filteredManagedProjects() {
        let projects = this.availableProjects;
        if (this.filterCategory) {
          projects = projects.filter(project => this.getProjectCategories(project).includes(this.filterCategory));
        }
        if (this.filterTitle) {
          projects = projects.filter(project => (project.title || '').toLowerCase().includes(this.filterTitle.toLowerCase()));
        }
        return projects.slice().sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      },
      filteredManagedProjectIds() {
        return this.filteredManagedProjects.map(project => project.id);
      },
      selectedManagedProjectIdsSet() {
        return new Set(this.selectedManagedProjectIds.map(id => String(id)));
      },
      managedProjectsToAdd() {
        const existingSubprojectProjectIdsSet = new Set(this.existingSubprojectProjectIds.map(id => String(id)));
        return this.availableProjects.filter((project) => {
          return this.selectedManagedProjectIdsSet.has(String(project.id)) && ! existingSubprojectProjectIdsSet.has(String(project.id));
        }).sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      },
      managedLanesToDelete() {
        return this.existingSubprojectLanes.filter(lane => ! this.selectedManagedProjectIdsSet.has(String(lane.project_id)));
      },
    },
    created() {
      this.selectedManagedProjectIds = this.existingSubprojectProjectIds.slice();
    },
    methods: {
      getProjectCategories(project) {
        return (project.category || '').split(',').map(item => item.trim()).filter(Boolean);
      },
      async manageSubprojects() {
        this.managingSubprojects = true;
        this.manageError = '';
        try {
          const projectsToAddWithContent = await Promise.all(this.managedProjectsToAdd.map(async (project) => {
            const content = await ProjectSrv.get(project.id);
            return { project, content };
          }));
          const lanesToAdd = projectsToAddWithContent.map(({ project, content }) => {
            return {
              label: project.title,
              project_id: project.id,
              project_rotoken: project.read_only_token,
              project_hidden_sublanes: this.newSubprojectsLanesCount ? content.lanes.slice(this.newSubprojectsLanesCount).map(lane => parseInt(lane.id, 10)) : [],
            };
          });
          this.planningLanesStore.updateAllLanes({ lanesToAdd, lanesToDelete: this.managedLanesToDelete });
          this.$emit('close');
        } catch (error) {
          this.manageError = String(error);
        } finally {
          this.managingSubprojects = false;
        }
      },
    },
  };
</script>
